
import { supabase } from "@/integrations/supabase/client";
import { format, getDay } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { getUserTimezone } from "./timezone-helpers";

/**
 * Fetches existing bookings for a practitioner on a specific date
 * @param practitionerId The ID of the practitioner
 * @param date The date to check
 * @returns Array of booking time periods [{start: string, end: string}]
 */
export const fetchPractitionerBookings = async (practitionerId: string, date: Date): Promise<{start: string, end: string}[]> => {
  if (!practitionerId || !date) {
    return [];
  }
  
  // Create date range for the selected date (00:00:00 to 23:59:59)
  const startDate = new Date(date);
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = new Date(date);
  endDate.setHours(23, 59, 59, 999);
  
  try {
    console.log("Fetching bookings for practitioner:", practitionerId, "on date:", date.toISOString());
    const { data, error } = await supabase
      .from('bookings')
      .select('start_time, end_time')
      .eq('practitioner_id', practitionerId)
      .gte('start_time', startDate.toISOString())
      .lte('start_time', endDate.toISOString())
      .in('status', ['Scheduled', 'Confirmed']); // Exclude 'Pending Payment' to avoid over-blocking availability
      
    if (error) {
      console.error('Error fetching practitioner bookings:', error);
      return [];
    }
    
    console.log("Retrieved bookings:", data?.length || 0, "slots");
    
    // Transform the data to match the expected return type
    return (data || []).map(booking => ({
      start: booking.start_time,
      end: booking.end_time
    }));
  } catch (error) {
    console.error('Error in fetchPractitionerBookings:', error);
    return [];
  }
};

/**
 * Checks if a time slot is available for booking
 * @param timeSlot Time slot to check (e.g. "09:00")
 * @param date Selected date
 * @param duration Duration in minutes
 * @param existingBookings Array of existing bookings
 * @returns boolean indicating if the slot is available
 */
export const isTimeSlotAvailable = (
  timeSlot: string,
  date: Date,
  duration: number,
  existingBookings: {start: string, end: string}[]
): boolean => {
  if (!timeSlot || !date) {
    return true;
  }

  // Create start and end time for the requested slot
  const [hours, minutes] = timeSlot.split(':').map(Number);
  const startTime = new Date(date);
  startTime.setHours(hours, minutes, 0, 0);
  
  const endTime = new Date(startTime.getTime() + (duration * 60000));
  
  // Check if this slot overlaps with any existing booking
  const isBooked = existingBookings.some(booking => {
    const bookingStart = new Date(booking.start);
    const bookingEnd = new Date(booking.end);

    // Normalize to minute precision to avoid boundary glitches
    bookingStart.setSeconds(0, 0);
    bookingEnd.setSeconds(0, 0);

    // Half-open interval overlap: [start, end)
    return (startTime < bookingEnd && endTime > bookingStart);
  });

  if (isBooked) {
    console.log(`Time slot ${timeSlot} is already booked`);
  }
  
  return !isBooked;
};

/**
 * Fetches practitioner availability rules
 * @param practitionerId The ID of the practitioner
 * @returns Availability rules object or null
 */
export const fetchPractitionerAvailability = async (practitionerId: string): Promise<any> => {
  if (!practitionerId) {
    return null;
  }
  
  try {
    console.log("Fetching availability rules for practitioner:", practitionerId);
    const { data, error } = await supabase
      .from('practitioners')
      .select('availability_rules')
      .eq('id', practitionerId)
      .single();
      
    if (error) {
      console.error('Error fetching practitioner availability:', error);
      return null;
    }
    
    return data?.availability_rules || null;
  } catch (error) {
    console.error('Error in fetchPractitionerAvailability:', error);
    return null;
  }
};

/**
 * Checks if a date is in the practitioner's blackout dates
 * @param date Date to check
 * @param availabilityRules Practitioner's availability rules
 * @param userTimezone User's timezone (optional, defaults to UTC)
 * @returns boolean indicating if the date is blacked out
 */
export const isDateBlackedOut = (
  date: Date, 
  availabilityRules: any, 
  userTimezone: string = 'UTC'
): boolean => {
  // Validate inputs - check for null, undefined, and invalid dates
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    console.warn('isDateBlackedOut: Invalid date provided:', date);
    return false;
  }
  
  if (!availabilityRules?.blackout_dates || !Array.isArray(availabilityRules.blackout_dates)) {
    return false;
  }
  
  // Validate timezone - ensure it's a valid string
  if (!userTimezone || typeof userTimezone !== 'string' || userTimezone.trim() === '') {
    console.warn('isDateBlackedOut: Invalid timezone provided:', userTimezone, 'falling back to UTC');
    userTimezone = 'UTC';
  }
  
  // Additional date validation - check for reasonable date ranges
  const year = date.getFullYear();
  if (year < 1900 || year > 2100) {
    console.warn('isDateBlackedOut: Date year out of reasonable range:', year);
    return false;
  }
  
  try {
    // Format the date in the user's timezone to match stored blackout dates
    const dateString = formatInTimeZone(date, userTimezone, 'yyyy-MM-dd');
    
    // Additional validation on the formatted string
    if (!dateString || typeof dateString !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      console.warn('isDateBlackedOut: Invalid formatted date string:', dateString);
      return false;
    }
    
    return availabilityRules.blackout_dates.includes(dateString);
  } catch (error) {
    console.error('isDateBlackedOut: Error formatting date:', error, { date, userTimezone });
    
    // Enhanced fallback - try multiple approaches
    try {
      // First fallback: basic format without timezone
      const fallbackDateString = format(date, 'yyyy-MM-dd');
      if (fallbackDateString && /^\d{4}-\d{2}-\d{2}$/.test(fallbackDateString)) {
        return availabilityRules.blackout_dates.includes(fallbackDateString);
      }
    } catch (fallbackError) {
      console.error('isDateBlackedOut: First fallback formatting failed:', fallbackError);
    }
    
    try {
      // Second fallback: manual date formatting
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const manualDateString = `${year}-${month}-${day}`;
      
      if (manualDateString && /^\d{4}-\d{2}-\d{2}$/.test(manualDateString)) {
        return availabilityRules.blackout_dates.includes(manualDateString);
      }
    } catch (manualError) {
      console.error('isDateBlackedOut: Manual fallback formatting failed:', manualError);
    }
    
    // If all formatting fails, return false (not blacked out)
    return false;
  }
};

/**
 * Checks if a time slot is within the practitioner's working hours for a given day
 * @param timeSlot Time slot to check (e.g. "09:00")
 * @param date Selected date
 * @param duration Duration in minutes
 * @param availabilityRules Practitioner's availability rules
 * @returns boolean indicating if the slot is within working hours
 */
export const isTimeSlotInWorkingHours = (
  timeSlot: string,
  date: Date,
  duration: number,
  availabilityRules: any
): boolean => {
  if (!availabilityRules?.weekly_schedule) {
    return true; // If no schedule is set, assume all times are available
  }
  
  // Map day of week (0 = Sunday, 1 = Monday, etc.) to our day names
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayOfWeek = getDay(date);
  const dayName = dayNames[dayOfWeek];
  
  const daySchedule = availabilityRules.weekly_schedule[dayName];
  
  // If the day is not enabled, no slots are available
  if (!daySchedule?.enabled) {
    return false;
  }
  
  // Parse the time slot
  const [hours, minutes] = timeSlot.split(':').map(Number);
  const slotMinutes = hours * 60 + minutes;
  const slotEndMinutes = slotMinutes + duration;
  
  // Check if the slot fits within any of the available time slots for the day
  return daySchedule.slots?.some((slot: any) => {
    const [startHours, startMinutes] = slot.start.split(':').map(Number);
    const [endHours, endMinutes] = slot.end.split(':').map(Number);
    
    const slotStartMinutes = startHours * 60 + startMinutes;
    const slotEndMinutesSchedule = endHours * 60 + endMinutes;
    
    // Check if the booking slot fits completely within the available slot
    return slotMinutes >= slotStartMinutes && slotEndMinutes <= slotEndMinutesSchedule;
  }) || false;
};

/**
 * Comprehensive check if a time slot is available considering both bookings and availability rules
 * @param timeSlot Time slot to check (e.g. "09:00")
 * @param date Selected date
 * @param duration Duration in minutes
 * @param existingBookings Array of existing bookings
 * @param availabilityRules Practitioner's availability rules
 * @param userTimezone User's timezone (optional, defaults to UTC)
 * @returns object with availability status and reason
 */
export const isTimeSlotCompletelyAvailable = (
  timeSlot: string,
  date: Date,
  duration: number,
  existingBookings: {start: string, end: string}[],
  availabilityRules: any,
  userTimezone: string = 'UTC'
): { available: boolean; reason?: string } => {
  if (!timeSlot || !date) {
    return { available: true };
  }

  // Check if time slot is in the past
  const [hours, minutes] = timeSlot.split(':').map(Number);
  const slotDateTime = new Date(date);
  slotDateTime.setHours(hours, minutes, 0, 0);
  
  const now = new Date();
  
  if (slotDateTime <= now) {
    return { available: false, reason: 'past' };
  }

  // Check if date is blacked out (with timezone consideration)
  if (isDateBlackedOut(date, availabilityRules, userTimezone)) {
    return { available: false, reason: 'unavailable' };
  }

  // Check if time is within working hours
  if (!isTimeSlotInWorkingHours(timeSlot, date, duration, availabilityRules)) {
    return { available: false, reason: 'unavailable' };
  }

  // Check if time conflicts with existing bookings
  if (!isTimeSlotAvailable(timeSlot, date, duration, existingBookings)) {
    return { available: false, reason: 'booked' };
  }

  return { available: true };
};
